import React, { useMemo, useState } from 'react';

interface PaginatedListProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  itemsPerPage?: number;
  containerClassName?: string;
  keyExtractor?: (item: T, index: number) => string | number;
}

const PaginatedList = <T,>({
  items,
  renderItem,
  itemsPerPage = 12,
  containerClassName = '',
  keyExtractor,
}: PaginatedListProps<T>) => {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(items.length / itemsPerPage)),
    [items, itemsPerPage]
  );
  const safePage = Math.min(currentPage, totalPages);
  if (safePage !== currentPage && currentPage !== 1) {
    setCurrentPage(safePage);
  }

  const startIndex = (safePage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = items.slice(startIndex, endIndex);

  if (items.length === 0) return null;

  const handlePrev = () => {
    setCurrentPage((p) => Math.max(1, p - 1));
  };

  const handleNext = () => {
    setCurrentPage((p) => Math.min(totalPages, p + 1));
  };

  return (
    <div className="flex flex-col gap-4">
      <div className={containerClassName}>
        {currentItems.map((item, index) => (
          <React.Fragment
            key={keyExtractor ? keyExtractor(item, startIndex + index) : startIndex + index}
          >
            {renderItem(item, startIndex + index)}
          </React.Fragment>
        ))}
      </div>
      {totalPages > 1 && (
        <div className="sizeer flex justify-center gap-4 pt-2">
          <button
            type="button"
            disabled={safePage <= 1}
            onClick={handlePrev}
            className={`flex size-[32px] items-center justify-center rounded-[6px] border text-sm font-medium transition-colors ${
              safePage <= 1
                ? 'cursor-not-allowed border-grey-200 text-grey-300'
                : 'border-grey-300 text-grey-600 hover:border-primary-400 hover:text-primary-500'
            }`}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M7.5 2.5L4.5 6L7.5 9.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <span className="select-none align-middle text-body-regular-14 text-grey-500">
            {safePage} / {totalPages}
          </span>

          <button
            type="button"
            disabled={safePage >= totalPages}
            onClick={handleNext}
            className={`flex size-[32px] items-center justify-center rounded-[6px] border text-sm font-medium transition-colors ${
              safePage >= totalPages
                ? 'cursor-not-allowed border-grey-200 text-grey-300'
                : 'border-grey-300 text-grey-600 hover:border-primary-400 hover:text-primary-500'
            }`}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M4.5 2.5L7.5 6L4.5 9.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default PaginatedList;
